import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Wellis brand palette, matching the rest of this page's inline styles.
const PDF_GOLD = [201, 169, 97]; // #c9a961
const PDF_DARK_GREEN = [13, 40, 24]; // #0d2818
const PDF_MID_GREEN = [26, 58, 42]; // #1a3a2a
const PDF_TEXT = [184, 166, 143]; // #b8a68f

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.wellis_id || m.affiliate_code || '—'})`;
}

const PAYMENT_METHOD_LABELS = {
  stripe: ['Stripe (Card)', 'Stripe（信用卡）'],
  sales_team_credit: ['Sales Team Credit', '销售团队认购积分'],
  credit_points: ['Credit Points', '认购积分自助购买'],
  manual_bank_transfer: ['Manual / Bank Transfer', '人工确认 / 银行转账'],
};

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function AttachmentsModal({ orderId, token, tt, onClose }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/package-order-attachments?order_id=${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) setAttachments(data.attachments || []);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, token]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  const remainingSlots = Math.max(MAX_ATTACHMENTS - attachments.length, 0);

  const handleFileChange = (e) => {
    setError('');
    const selected = Array.from(e.target.files || []);
    if (selected.length > remainingSlots) {
      setError(tt(`You can add at most ${remainingSlots} more image(s) to this order.`, `此订单最多还能添加 ${remainingSlots} 张图片。`));
      setFiles([]);
      return;
    }
    const oversized = selected.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversized) {
      setError(tt('Each image must be 10MB or smaller.', '每张图片不能超过 10MB。'));
      setFiles([]);
      return;
    }
    const nonImage = selected.find((f) => !f.type.startsWith('image/'));
    if (nonImage) {
      setError(tt('Only image files are allowed.', '只能上传图片文件。'));
      setFiles([]);
      return;
    }
    setFiles(selected);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('order_id', orderId);
      files.forEach((f) => form.append('images', f));
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/package-order-attachments/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Upload failed');
      setFiles([]);
      await fetchAttachments();
    } catch (err) {
      setError(err.message || tt('Failed to upload images.', '图片上传失败。'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-8">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{tt('Order Attachments', '订单附件')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          {loading ? (
            <p className="text-sm text-gray-500">{tt('Loading...', '加载中...')}</p>
          ) : attachments.length === 0 ? (
            <p className="text-sm text-gray-500">{tt('No images uploaded yet.', '暂无已上传图片。')}</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {attachments.map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
                  <img src={a.url} alt="" className="w-full h-20 object-cover rounded border border-gray-200" />
                </a>
              ))}
            </div>
          )}

          {remainingSlots > 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tt(`Add up to ${remainingSlots} more image(s) (10MB max each)`, `最多再添加 ${remainingSlots} 张图片（每张不超过 10MB）`)}
              </label>
              <input type="file" accept="image/*" multiple onChange={handleFileChange} disabled={uploading} />
              {files.length > 0 && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-3 w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {uploading ? tt('Uploading...', '上传中...') : tt(`Upload ${files.length} image(s)`, `上传 ${files.length} 张图片`)}
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400">{tt('Maximum of 5 images reached for this order.', '此订单已达到 5 张图片的上限。')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [processingOrderId, setProcessingOrderId] = useState(null);
  const [attachmentsOrderId, setAttachmentsOrderId] = useState(null);
  const [pdfStartDate, setPdfStartDate] = useState('');
  const [pdfEndDate, setPdfEndDate] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [expandedPassiveRun, setExpandedPassiveRun] = useState(null); // run_date currently expanded, or null
  const [passiveRunRecords, setPassiveRunRecords] = useState({}); // run_date -> records[] (cached)
  const [loadingPassiveRun, setLoadingPassiveRun] = useState(null); // run_date currently loading, or null

  const togglePassiveRunDetail = useCallback(async (runDate) => {
    if (expandedPassiveRun === runDate) {
      setExpandedPassiveRun(null);
      return;
    }
    setExpandedPassiveRun(runDate);
    if (passiveRunRecords[runDate]) return; // already cached
    setLoadingPassiveRun(runDate);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/list?date=${runDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load day detail');
      setPassiveRunRecords((prev) => ({ ...prev, [runDate]: json.records || [] }));
    } catch (err) {
      setPassiveRunRecords((prev) => ({ ...prev, [runDate]: { error: err.message } }));
    } finally {
      setLoadingPassiveRun(null);
    }
  }, [expandedPassiveRun, passiveRunRecords, token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Failed to load reports');
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchReport();
  }, [user, isAdmin, fetchReport]);

  const handleAccept = async (orderId) => {
    setProcessingOrderId(orderId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/packages/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to accept order');
      await fetchReport();
    } catch (err) {
      setError(err.message || 'Failed to accept order');
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleReject = async (orderId) => {
    const reason = window.prompt(tt('Reason for rejection:', '拒绝原因：'), '');
    if (reason === null) return;
    setProcessingOrderId(orderId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/packages/reject-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to reject order');
      await fetchReport();
    } catch (err) {
      setError(err.message || 'Failed to reject order');
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleReverse = async (orderId) => {
    const reason = window.prompt(
      tt(
        'Reverse this sale? This cancels the order and claws back any Direct Referral / Passive amounts it generated. Reason:',
        '确定要撤销此销售吗？这将取消订单，并追回其产生的直推佣金/趸缴分红。原因：'
      ),
      ''
    );
    if (reason === null) return;
    setProcessingOrderId(orderId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports/reverse-package-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ order_id: orderId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to reverse sale');

      const parts = [tt('Sale reversed.', '销售已撤销。')];
      if (data.direct_referral_clawed_back > 0) {
        parts.push(tt(`Direct Referral clawed back: ${fmt(data.direct_referral_clawed_back)}`, `已追回直推佣金：${fmt(data.direct_referral_clawed_back)}`));
      }
      if (data.passive_clawed_back > 0) {
        parts.push(tt(`Passive clawed back: ${fmt(data.passive_clawed_back)}`, `已追回趸缴分红：${fmt(data.passive_clawed_back)}`));
      }
      if (data.still_has_tree_placement) {
        parts.push(tt(
          'Note: this member still has a tree placement -- reversing a sale does not remove them from the tree. Use /genealogy if that also needs to change.',
          '注意：该会员在系谱树中仍有位置 —— 撤销销售不会将其从系谱树中移除。如需变更，请前往 /genealogy。'
        ));
      }
      window.alert(parts.join('\n\n'));
      await fetchReport();
    } catch (err) {
      setError(err.message || 'Failed to reverse sale');
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleReversePreProcessed = async (creditTransactionId) => {
    const reason = window.prompt(
      tt(
        'Reverse this pre-processed grant? This claws back the original grant amount plus any Direct Referral / Passive amounts it generated. Reason:',
        '确定要撤销此预处理发放吗？这将追回原始发放金额，以及其产生的直推佣金/趸缴分红。原因：'
      ),
      ''
    );
    if (reason === null) return;
    setProcessingOrderId(creditTransactionId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports/reverse-preprocessed-grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ credit_transaction_id: creditTransactionId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to reverse grant');

      const parts = [tt('Grant reversed.', '发放已撤销。')];
      if (data.grant_clawed_back > 0) {
        parts.push(tt(`Grant clawed back: ${fmt(data.grant_clawed_back)}`, `已追回发放金额：${fmt(data.grant_clawed_back)}`));
      }
      if (data.direct_referral_clawed_back > 0) {
        parts.push(tt(`Direct Referral clawed back: ${fmt(data.direct_referral_clawed_back)}`, `已追回直推佣金：${fmt(data.direct_referral_clawed_back)}`));
      }
      if (data.passive_clawed_back > 0) {
        parts.push(tt(`Passive clawed back: ${fmt(data.passive_clawed_back)}`, `已追回趸缴分红：${fmt(data.passive_clawed_back)}`));
      }
      if (data.still_has_tree_placement) {
        parts.push(tt(
          'Note: this member still has a tree placement -- reversing this grant does not remove them from the tree. Use /genealogy if that also needs to change.',
          '注意：该会员在系谱树中仍有位置 —— 撤销此发放不会将其从系谱树中移除。如需变更，请前往 /genealogy。'
        ));
      }
      window.alert(parts.join('\n\n'));
      await fetchReport();
    } catch (err) {
      setError(err.message || 'Failed to reverse grant');
    } finally {
      setProcessingOrderId(null);
    }
  };

  if (!user || !isAdmin) return null;

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt0 = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });

  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const rowStyle = { backgroundColor: '#152214', borderColor: '#3d5a4a' };

  const totalUsers = data?.userStatusCounts ? data.userStatusCounts.approved + data.userStatusCounts.verified : 0;
  const totalOrders = data?.orderStatusCounts
    ? data.orderStatusCounts.pending + data.orderStatusCounts.confirmed + data.orderStatusCounts.rejected + data.orderStatusCounts.cancelled
    : 0;
  const maxPackageRevenue = data?.packageBreakdown?.length ? Math.max(...data.packageBreakdown.map((p) => p.revenue)) : 0;
  const maxTrendAmount = data?.dailyTrend?.length ? Math.max(...data.dailyTrend.map((d) => d.amount)) : 0;
  const searchLower = transactionSearch.trim().toLowerCase();
  const filteredTransactions = (data?.transactions || []).filter((t) => {
    if (!searchLower) return true;
    const haystack = `${t.wellis_id || ''} ${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
    return haystack.includes(searchLower);
  });
  const transactionsTotal = filteredTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const pdfDateFilteredTransactions = (data?.transactions || []).filter((t) => {
    const day = (t.created_at || '').slice(0, 10);
    if (!day) return false;
    if (pdfStartDate && day < pdfStartDate) return false;
    if (pdfEndDate && day > pdfEndDate) return false;
    return true;
  }).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));

  // Strips the "WELLIS-" prefix for compact display, e.g. in a printed
  // table column -- "WELLIS-ABCD1234" -> "ABCD1234".
  const shortId = (wellisId) => (wellisId ? wellisId.replace(/^WELLIS-/i, '') : wellisId);

  const handleGeneratePdf = () => {
    setGeneratingPdf(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Full dark-green page background (matches the in-app look), gold
      // headings, light body text. jsPDF has no persistent page
      // background -- each new page starts blank -- so this must be
      // repainted whenever autoTable starts a NEW page (page 2+), but
      // NOT via willDrawPage/didDrawPage on page 1, since those fire
      // after the header band/title above are already drawn and would
      // paint over them with an opaque rect.
      const paintPageBackground = () => {
        doc.setFillColor(...PDF_MID_GREEN);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };
      paintPageBackground();

      // Header band: darker green, gold title text.
      doc.setFillColor(...PDF_DARK_GREEN);
      doc.rect(0, 0, pageWidth, 70, 'F');
      doc.setTextColor(...PDF_GOLD);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('Wellis', 40, 32);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(tt('Sales Report - All Transactions', '销售报告 - 全部交易'), 40, 50);

      const rangeLabel = pdfStartDate || pdfEndDate
        ? `${pdfStartDate || tt('Start', '起始')} — ${pdfEndDate || tt('Now', '至今')}`
        : tt('All dates', '所有日期');
      doc.setFontSize(9);
      doc.setTextColor(...PDF_TEXT);
      doc.text(rangeLabel, pageWidth - 40, 32, { align: 'right' });
      doc.text(`${tt('Generated', '生成时间')}: ${new Date().toLocaleString()}`, pageWidth - 40, 46, { align: 'right' });

      const rows = pdfDateFilteredTransactions.map((t, idx) => {
        const isPreProcessed = t.status === 'pre_processed';
        const statusLabel = isPreProcessed ? tt('Pre-Processed', '预处理') : t.status;
        const methodLabel = isPreProcessed ? '—' : (PAYMENT_METHOD_LABELS[t.payment_method] ? tt(...PAYMENT_METHOD_LABELS[t.payment_method]) : (t.payment_method || '—'));
        return [
          idx + 1,
          memberLabel(t),
          t.sponsor_wellis_id ? `${t.sponsor_name || '—'} (${shortId(t.sponsor_wellis_id)})` : '—',
          t.parent_wellis_id ? `${shortId(t.parent_wellis_id)} - ${t.leg_position || '—'}` : '—',
          `${t.package_name || '—'}${t.package_code ? ` (${t.package_code})` : ''}`,
          fmt(t.amount),
          statusLabel,
          methodLabel,
          (t.created_at || '').slice(0, 10),
        ];
      });

      autoTable(doc, {
        startY: 90,
        head: [[
          tt('S/N', '序号'), tt('Member', '会员'), tt('Sponsor', '推荐人'), tt('Gentree Pos.', '排位'),
          tt('Package', '套餐'), tt('Amount', '金额'), tt('Status', '状态'),
          tt('Method', '方式'), tt('Date', '日期'),
        ]],
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: PDF_DARK_GREEN, textColor: PDF_GOLD, fontStyle: 'bold', fontSize: 8, lineColor: PDF_GOLD, lineWidth: 0.5 },
        bodyStyles: { fontSize: 7.5, textColor: PDF_TEXT, lineColor: PDF_GOLD, lineWidth: 0.5 },
        alternateRowStyles: { fillColor: PDF_DARK_GREEN },
        margin: { left: 30, right: 30 },
        willDrawPage: (data) => {
          // Only repaint on pages AFTER the first -- page 1's background
          // is already painted above, before the header band/title text
          // that this callback would otherwise cover.
          if (data.pageNumber > 1) paintPageBackground();
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(...PDF_GOLD);
          doc.text(
            `${tt('Page', '第')} ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`,
            pageWidth - 40, pageHeight - 20, { align: 'right' }
          );
        },
      });

      const finalY = doc.lastAutoTable.finalY || 90;
      doc.setFontSize(10);
      doc.setTextColor(...PDF_GOLD);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${tt('Total', '总计')}: ${fmt(pdfDateFilteredTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0))}`,
        30, finalY + 20
      );

      const filenameRange = pdfStartDate || pdfEndDate
        ? `${pdfStartDate || 'start'}_to_${pdfEndDate || 'now'}`
        : 'all';
      doc.save(`wellis-sales-report-${filenameRange}.pdf`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-1" style={{ color: '#c9a961' }}>
          {tt('Sales Reports Dashboard', '销售报告仪表板')}
        </h1>
        <p className="text-body-xs mb-6" style={{ color: '#999999' }}>
          {tt(
            'Built directly on live package_orders / users data — every purchase (Stripe, Credit Points, sales-team, or admin-confirmed) feeds this automatically.',
            '直接基于实时的 package_orders / users 数据构建——每一笔购买（Stripe、认购积分、销售团队或人工确认）都会自动汇入此报告。'
          )}
        </p>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        {loading || !data ? (
          <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
        ) : (
          <>
            {/* Executive KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-2">
              {[
                [tt('Total Confirmed Sales', '已确认销售总额'), fmt(data.kpis.total_sales)],
                [tt('Pre-Processed Sales', '预处理销售额'), fmt(data.kpis.total_preprocessed_sales)],
                [tt('Transactions', '交易笔数'), fmt0(data.kpis.transaction_count)],
                [tt('Average Sale', '平均单笔金额'), fmt(data.kpis.average_sale)],
                [tt('Collection Rate', '成交率'), `${fmt0(data.kpis.collection_rate)}%`],
              ].map(([label, value]) => (
                <div key={label} className="border rounded-lg p-4 text-center" style={cardStyle}>
                  <div className="text-body-xs mb-1" style={{ color: '#999999' }}>{label}</div>
                  <div className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>{value}</div>
                </div>
              ))}
            </div>
            <p className="text-body-xs mb-8" style={{ color: '#999999' }}>
              {tt(
                'Pre-Processed Sales are admin-granted credit before a package was purchased -- a separate category from Confirmed Sales, not a subset of it. Other systems (e.g. Passive) tabulate against the combined total of both.',
                '预处理销售额是指在会员购买套餐之前由管理员授予的积分——这是与已确认销售额分开的类别，而非其子集。其他系统（如被动收益）按两者合计计算。'
              )}
            </p>

            {/* Daily sales trend */}
            <div className="border rounded-lg p-6 mb-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Daily Sales Trend', '每日销售趋势')}</h3>
              {data.dailyTrend.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No confirmed sales yet.', '暂无已确认销售。')}</p>
              ) : (
                <div className="space-y-1">
                  {data.dailyTrend.map((d) => (
                    <div key={d.day} className="flex items-center gap-3">
                      <span className="text-body-xs w-24 flex-shrink-0" style={{ color: '#999999' }}>{d.day}</span>
                      <div className="flex-1 rounded h-4 overflow-hidden" style={{ backgroundColor: '#152214' }}>
                        <div
                          className="h-full rounded"
                          style={{ backgroundColor: '#c9a961', width: maxTrendAmount > 0 ? `${(d.amount / maxTrendAmount) * 100}%` : '0%' }}
                        />
                      </div>
                      <span className="text-body-xs w-24 text-right flex-shrink-0" style={{ color: '#b8a68f' }}>{fmt(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {/* Package revenue breakdown */}
              <div className="border rounded-lg p-6" style={cardStyle}>
                <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Package Revenue', '套餐收入')}</h3>
                {data.packageBreakdown.length === 0 ? (
                  <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No confirmed sales yet.', '暂无已确认销售。')}</p>
                ) : (
                  <div className="space-y-2">
                    {data.packageBreakdown.map((p) => (
                      <div key={p.package_code}>
                        <div className="flex items-center justify-between text-body-sm mb-1">
                          <span style={{ color: '#b8a68f' }}>{p.package_name} ({p.package_code}) × {p.count}</span>
                          <span className="font-bold" style={{ color: '#c9a961' }}>{fmt(p.revenue)}</span>
                        </div>
                        <div className="rounded h-2 overflow-hidden" style={{ backgroundColor: '#152214' }}>
                          <div className="h-full rounded" style={{ backgroundColor: '#c9a961', width: maxPackageRevenue > 0 ? `${(p.revenue / maxPackageRevenue) * 100}%` : '0%' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment method breakdown */}
              <div className="border rounded-lg p-6" style={cardStyle}>
                <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Payment Methods', '支付方式')}</h3>
                {data.paymentMethodBreakdown.length === 0 ? (
                  <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No confirmed sales yet.', '暂无已确认销售。')}</p>
                ) : (
                  <div className="space-y-2">
                    {data.paymentMethodBreakdown.map((p) => {
                      const label = PAYMENT_METHOD_LABELS[p.method] ? tt(...PAYMENT_METHOD_LABELS[p.method]) : p.method;
                      return (
                        <div key={p.method} className="flex items-center justify-between p-2 rounded border" style={rowStyle}>
                          <span className="text-body-sm">{label} × {p.count}</span>
                          <span className="font-bold" style={{ color: '#c9a961' }}>{fmt(p.revenue)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Detailed transaction list */}
            <div className="border rounded-lg p-6 mb-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('All Transactions', '全部交易明细')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'Every package_orders row, individually -- use this to look up a specific member (e.g. cross-referencing against the genealogy tree). The sections above are all aggregates and won\'t show an individual member.',
                  '逐笔列出所有 package_orders 订单——用于查找特定会员（例如与家谱树进行核对）。以上各板块均为汇总数据，无法显示单个会员的记录。'
                )}
              </p>
              <input
                type="text"
                placeholder={tt('Search Wellis ID or name...', '搜索 Wellis ID 或姓名...')}
                value={transactionSearch}
                onChange={(e) => setTransactionSearch(e.target.value)}
                className="w-full px-3 py-2 mb-3 text-body-sm rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
              />

              <div className="border rounded-lg p-4 mb-4" style={rowStyle}>
                <p className="text-body-xs font-medium mb-2" style={{ color: '#c9a961' }}>
                  {tt('Print / Export PDF', '打印 / 导出 PDF')}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-body-xs mb-1" style={{ color: '#999999' }}>{tt('From', '起始')}</label>
                    <input
                      type="date"
                      value={pdfStartDate}
                      onChange={(e) => setPdfStartDate(e.target.value)}
                      className="px-2 py-1 text-body-sm rounded border"
                      style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
                    />
                  </div>
                  <div>
                    <label className="block text-body-xs mb-1" style={{ color: '#999999' }}>{tt('To', '截止')}</label>
                    <input
                      type="date"
                      value={pdfEndDate}
                      onChange={(e) => setPdfEndDate(e.target.value)}
                      className="px-2 py-1 text-body-sm rounded border"
                      style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf}
                    className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 text-black"
                    style={{ backgroundColor: '#c9a961' }}
                  >
                    {generatingPdf ? tt('Generating...', '生成中...') : tt('Download PDF', '下载 PDF')}
                  </button>
                  {(pdfStartDate || pdfEndDate) && (
                    <button
                      type="button"
                      onClick={() => { setPdfStartDate(''); setPdfEndDate(''); }}
                      className="px-3 py-2 text-body-xs underline"
                      style={{ color: '#999999' }}
                    >
                      {tt('Clear dates', '清除日期')}
                    </button>
                  )}
                </div>
                <p className="text-body-xs mt-2" style={{ color: '#999999' }}>
                  {tt(
                    'Leave dates blank to export all transactions. The PDF ignores the search box above and always includes every matching row.',
                    '留空日期将导出所有交易记录。PDF 不受上方搜索框影响，始终包含所有符合条件的记录。'
                  )}
                </p>
              </div>

              <div className="flex items-center justify-between p-3 mb-3 rounded border" style={rowStyle}>
                <span className="text-body-sm" style={{ color: '#999999' }}>
                  {tt('Total System Sales (shown below)', '系统销售总额（下表所示）')}
                </span>
                <span className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>{fmt(transactionsTotal)}</span>
              </div>
              {filteredTransactions.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No matching transactions.', '没有匹配的交易记录。')}</p>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('S/N', '序号')}</th>
                        <th className="p-2">{tt('Member', '会员')}</th>
                        <th className="p-2">{tt('Sponsor', '推荐人')}</th>
                        <th className="p-2">{tt('Gentree Position', '排位')}</th>
                        <th className="p-2">{tt('Package', '套餐')}</th>
                        <th className="p-2">{tt('Amount', '金额')}</th>
                        <th className="p-2">{tt('Status', '状态')}</th>
                        <th className="p-2">{tt('Payment Method', '支付方式')}</th>
                        <th className="p-2">{tt('Date', '日期')}</th>
                        <th className="p-2">{tt('Actions', '操作')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((t, idx) => {
                        const isPreProcessed = t.status === 'pre_processed';
                        const statusColor = t.status === 'confirmed' ? '#c9a961' : t.status === 'rejected' ? '#c94961' : isPreProcessed ? '#d97706' : '#999999';
                        const statusLabel = isPreProcessed ? tt('Pre-Processed', '预处理') : t.status;
                        const methodLabel = isPreProcessed ? '—' : (PAYMENT_METHOD_LABELS[t.payment_method] ? tt(...PAYMENT_METHOD_LABELS[t.payment_method]) : t.payment_method);
                        const isProcessing = processingOrderId === t.id;
                        return (
                          <tr key={t.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                            <td className="p-2" style={{ color: '#999999' }}>{idx + 1}</td>
                            <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(t)}</td>
                            <td className="p-2">
                              {t.sponsor_wellis_id
                                ? `${t.sponsor_name || '—'} (${shortId(t.sponsor_wellis_id)})`
                                : '—'}
                            </td>
                            <td className="p-2">
                              {t.parent_wellis_id
                                ? `${shortId(t.parent_wellis_id)} - ${t.leg_position || '—'}`
                                : '—'}
                            </td>
                            <td className="p-2">{t.package_name || '—'}{t.package_code ? ` (${t.package_code})` : ''}</td>
                            <td className="p-2">{fmt(t.amount)}</td>
                            <td className="p-2" style={{ color: statusColor }}>{statusLabel}</td>
                            <td className="p-2">{methodLabel}</td>
                            <td className="p-2">{(t.created_at || '').slice(0, 10)}</td>
                            <td className="p-2">
                              <div className="flex gap-1 flex-wrap">
                                {!isPreProcessed && t.status === 'pending' && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleAccept(t.id)}
                                      disabled={isProcessing}
                                      className="px-2 py-1 rounded text-body-xs font-medium disabled:opacity-50"
                                      style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                                    >
                                      {tt('Accept', '接受')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleReject(t.id)}
                                      disabled={isProcessing}
                                      className="px-2 py-1 rounded text-body-xs font-medium border disabled:opacity-50"
                                      style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                                    >
                                      {tt('Reject', '拒绝')}
                                    </button>
                                  </>
                                )}
                                {!isPreProcessed && (
                                  <button
                                    type="button"
                                    onClick={() => setAttachmentsOrderId(t.id)}
                                    className="px-2 py-1 rounded text-body-xs font-medium border"
                                    style={{ backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
                                  >
                                    {tt('Add Info', '添加资料')}
                                  </button>
                                )}
                                {user.role === 'superadmin' && !isPreProcessed && t.status === 'confirmed' && (
                                  <button
                                    type="button"
                                    onClick={() => handleReverse(t.id)}
                                    disabled={isProcessing}
                                    className="px-2 py-1 rounded text-body-xs font-medium border disabled:opacity-50"
                                    style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                                    title={tt('Cancel this order and claw back any Direct Referral/Passive it generated', '取消此订单，并追回其产生的直推佣金/趸缴分红')}
                                  >
                                    {tt('Reverse Sale', '撤销销售')}
                                  </button>
                                )}
                                {user.role === 'superadmin' && isPreProcessed && (
                                  <button
                                    type="button"
                                    onClick={() => handleReversePreProcessed(t.id)}
                                    disabled={isProcessing}
                                    className="px-2 py-1 rounded text-body-xs font-medium border disabled:opacity-50"
                                    style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                                    title={tt('Claw back this pre-processed grant plus any Direct Referral/Passive it generated', '追回此预处理发放金额及其产生的直推佣金/趸缴分红')}
                                  >
                                    {tt('Reverse Grant', '撤销发放')}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sales Team credit processes */}
            <div className="border rounded-lg p-6 mb-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Sales Team — Credit Processes', '销售团队 — 认购积分处理')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'Signups funded by each sales-team member via /signup-internal, and how much of that spend has been remitted back.',
                  '每位销售团队成员通过 /signup-internal 资助的注册，以及已汇回公司的金额。'
                )}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                {[
                  [tt('Members', '成员数'), data.salesTeam.length],
                  [tt('Signups Funded', '资助注册数'), data.salesTeamTotals.signups_count],
                  [tt('Total Credit Spent', '认购积分总支出'), fmt(data.salesTeamTotals.total_credit_spent)],
                  [tt('Remitted (Confirmed)', '已汇回（已确认）'), fmt(data.salesTeamTotals.confirmed_remitted)],
                  [tt('Outstanding', '未结清'), fmt(data.salesTeamTotals.outstanding)],
                ].map(([label, value]) => (
                  <div key={label} className="p-3 rounded border text-center" style={rowStyle}>
                    <div className="text-body-xs" style={{ color: '#999999' }}>{label}</div>
                    <div className="font-bold" style={{ color: '#c9a961' }}>{value}</div>
                  </div>
                ))}
              </div>

              {data.salesTeam.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No sales-team members yet.', '暂无销售团队成员。')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('Member', '成员')}</th>
                        <th className="p-2">{tt('Credit Balance', '认购积分余额')}</th>
                        <th className="p-2">{tt('Signups', '注册数')}</th>
                        <th className="p-2">{tt('Credit Spent', '积分支出')}</th>
                        <th className="p-2">{tt('Remitted', '已汇回')}</th>
                        <th className="p-2">{tt('Outstanding', '未结清')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.salesTeam.map((m) => (
                        <tr key={m.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(m)}</td>
                          <td className="p-2">{fmt(m.credit_balance)}</td>
                          <td className="p-2">{m.signups_count}</td>
                          <td className="p-2">{fmt(m.total_credit_spent)}</td>
                          <td className="p-2">{fmt(m.confirmed_remitted)}</td>
                          <td className="p-2" style={{ color: m.outstanding > 0 ? '#d97706' : '#b8a68f' }}>{fmt(m.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Approved / Verified statuses */}
            <div className="border rounded-lg p-6" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Approved vs. Verified (Verified = Paid)', '已批准 vs. 已验证（已验证 = 已付款）')}
              </h3>
              <p className="text-body-xs mb-1" style={{ color: '#999999' }}>
                {tt(
                  '"Verified" means the member has an active package (package_id set) -- i.e. payment has actually been collected. "Approved" is everyone who signed up but hasn\'t completed a paid purchase yet.',
                  '"已验证"指该会员已有生效套餐（已设置 package_id）——即已实际收到付款。"已批准"指已注册但尚未完成付费购买的会员。'
                )}
              </p>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'These two tables count different things and won\'t match 1:1: the left counts unique members; the right counts individual orders (one member can have more than one order, e.g. an upgrade).',
                  '这两个表格统计的对象不同，数字不会一一对应：左边统计的是唯一会员数；右边统计的是订单笔数（一位会员可能有多笔订单，例如套餐升级）。'
                )}
              </p>

              {data.statusMismatchCount > 0 ? (
                <div className="border rounded p-3 mb-4 text-body-xs" style={{ backgroundColor: '#2a1d0d', borderColor: '#d97706', color: '#d97706' }}>
                  {tt(
                    `⚠ Data integrity: ${data.statusMismatchCount} member(s) have a users.status value that disagrees with their actual package_id -- the counts below use package_id (the reliable signal), not the status field.`,
                    `⚠ 数据一致性提醒：有 ${data.statusMismatchCount} 位会员的 users.status 字段与其实际 package_id 不一致——下方统计使用 package_id（更可靠的字段），而非 status 字段。`
                  )}
                </div>
              ) : (
                <div className="border rounded p-3 mb-4 text-body-xs" style={{ backgroundColor: '#0d2818', borderColor: '#3d5a4a', color: '#999999' }}>
                  {tt('✓ users.status is fully consistent with package_id for all members.', '✓ 所有会员的 users.status 均与 package_id 一致。')}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-body-sm font-semibold mb-2" style={{ color: '#c9a961' }}>{tt('Members by Status (unique members)', '会员状态分布（唯一会员数）')}</h4>
                  <div className="space-y-2">
                    {[
                      [tt('Approved (not yet paid)', '已批准（尚未付款）'), data.userStatusCounts.approved],
                      [tt('Verified (paid)', '已验证（已付款）'), data.userStatusCounts.verified],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between p-2 rounded border" style={rowStyle}>
                        <span className="text-body-sm">{label}</span>
                        <span className="font-bold" style={{ color: '#c9a961' }}>
                          {value} {totalUsers > 0 && <span className="text-body-xs" style={{ color: '#999999' }}>({((value / totalUsers) * 100).toFixed(1)}%)</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-2 rounded border" style={rowStyle}>
                      <span className="text-body-sm font-semibold">{tt('Total Members', '会员总数')}</span>
                      <span className="font-bold" style={{ color: '#c9a961' }}>{totalUsers}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-body-sm font-semibold mb-2" style={{ color: '#c9a961' }}>{tt('Package Orders by Status (individual orders)', '套餐订单状态分布（订单笔数）')}</h4>
                  <div className="space-y-2">
                    {[
                      [tt('Pending', '待处理'), data.orderStatusCounts.pending],
                      [tt('Confirmed (Paid)', '已确认（已付款）'), data.orderStatusCounts.confirmed],
                      [tt('Rejected', '已拒绝'), data.orderStatusCounts.rejected],
                      [tt('Cancelled', '已取消'), data.orderStatusCounts.cancelled],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between p-2 rounded border" style={rowStyle}>
                        <span className="text-body-sm">{label}</span>
                        <span className="font-bold" style={{ color: '#c9a961' }}>
                          {value} {totalOrders > 0 && <span className="text-body-xs" style={{ color: '#999999' }}>({((value / totalOrders) * 100).toFixed(1)}%)</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-2 rounded border" style={rowStyle}>
                      <span className="text-body-sm font-semibold">{tt('Total Orders', '订单总数')}</span>
                      <span className="font-bold" style={{ color: '#c9a961' }}>{totalOrders}</span>
                    </div>
                  </div>
                </div>
              </div>

              {data.packageStatusBreakdown.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-body-sm font-semibold mb-2" style={{ color: '#c9a961' }}>{tt('Orders by Package', '各套餐订单明细')}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                      <thead>
                        <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                          <th className="p-2">{tt('Package', '套餐')}</th>
                          <th className="p-2">{tt('Pending', '待处理')}</th>
                          <th className="p-2">{tt('Confirmed', '已确认')}</th>
                          <th className="p-2">{tt('Rejected', '已拒绝')}</th>
                          <th className="p-2">{tt('Cancelled', '已取消')}</th>
                          <th className="p-2">{tt('Total', '总计')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.packageStatusBreakdown.map((p) => (
                          <tr key={p.package_code} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                            <td className="p-2" style={{ color: '#c9a961' }}>{p.package_name} ({p.package_code})</td>
                            <td className="p-2">{p.pending}</td>
                            <td className="p-2">{p.confirmed}</td>
                            <td className="p-2">{p.rejected}</td>
                            <td className="p-2">{p.cancelled}</td>
                            <td className="p-2 font-semibold" style={{ color: '#c9a961' }}>{p.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Passive (Company Pool) run history */}
            <div className="border rounded-lg p-6 mt-8" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Passive — Run History', '被动 — 计算记录')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'One row per calendar day the Passive pool was calculated. "Day 0" is 2026-08-15, the first day.',
                  '被动奖金池每次按日计算的记录，一天一行。"Day 0" 为 2026-08-15，即第一天。'
                )}
              </p>
              {(data.passiveRuns || []).length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No Passive runs yet.', '暂无被动计算记录。')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2"></th>
                        <th className="p-2">{tt('Run', '批次')}</th>
                        <th className="p-2">{tt('Date', '日期')}</th>
                        <th className="p-2">{tt('New Sales', '本期新增销售额')}</th>
                        <th className="p-2">{tt('Total Allocations', '累计分配单位')}</th>
                        <th className="p-2">{tt('Rate', '比例')}</th>
                        <th className="p-2">{tt('Pool Total', '奖金池总额')}</th>
                        <th className="p-2">{tt('Orders', '订单数')}</th>
                        <th className="p-2">{tt('Calculated At', '计算时间')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.passiveRuns.map((r) => {
                        const isExpanded = expandedPassiveRun === r.run_date;
                        const dayRecords = passiveRunRecords[r.run_date];
                        return (
                          <React.Fragment key={r.id}>
                            <tr className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                              <td className="p-2">
                                <button
                                  type="button"
                                  onClick={() => togglePassiveRunDetail(r.run_date)}
                                  className="px-2 py-1 rounded text-body-xs font-medium"
                                  style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}
                                  title={tt('Show who was paid this day', '显示当日分配明细')}
                                >
                                  {isExpanded ? '▾' : '▸'} {tt('Distribution', '分配明细')}
                                </button>
                              </td>
                              <td className="p-2 font-semibold" style={{ color: '#c9a961' }}>{r.day_label}</td>
                              <td className="p-2">{r.run_date}</td>
                              <td className="p-2">{fmt(r.total_sales)}</td>
                              <td className="p-2">{fmt(r.total_allocations)}</td>
                              <td className="p-2">{fmt0(r.rate_percent_snapshot)}%</td>
                              <td className="p-2 font-semibold" style={{ color: '#c9a961' }}>{fmt(r.pool_total)}</td>
                              <td className="p-2">{r.orders_included}</td>
                              <td className="p-2">{(r.calculated_at || '').slice(0, 16).replace('T', ' ')}</td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={9} className="p-3" style={{ backgroundColor: '#152214' }}>
                                  {loadingPassiveRun === r.run_date ? (
                                    <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
                                  ) : dayRecords?.error ? (
                                    <p className="text-body-sm" style={{ color: '#e05252' }}>{dayRecords.error}</p>
                                  ) : !dayRecords || dayRecords.length === 0 ? (
                                    <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                                      {tt('No records for this day (nothing was tabulated strictly before it yet).', '当日暂无分配记录（尚无更早的份额参与分配）。')}
                                    </p>
                                  ) : (
                                    <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                                      <thead>
                                        <tr className="text-left border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                                          <th className="p-1">{tt('Recipient', '接收人')}</th>
                                          <th className="p-1">{tt('Package', '配套')}</th>
                                          <th className="p-1">{tt('Amount', '金额')}</th>
                                          <th className="p-1">{tt('Status', '状态')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {dayRecords.map((rec) => (
                                          <tr key={rec.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.1)' }}>
                                            <td className="p-1" style={{ color: '#c9a961' }}>{memberLabel(rec.recipient)}</td>
                                            <td className="p-1">{rec.package_code || '—'}</td>
                                            <td className="p-1 font-semibold" style={{ color: '#c9a961' }}>{fmt(rec.amount)}</td>
                                            <td className="p-1">{rec.status}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {attachmentsOrderId && (
        <AttachmentsModal
          orderId={attachmentsOrderId}
          token={token}
          tt={tt}
          onClose={() => setAttachmentsOrderId(null)}
        />
      )}
    </div>
  );
}
